import { setTopView, rotateAround }  from '../Minimap3D/MinimapMath.js';
import { mapPixelsToWorld, widgetToMapPixels, getCameraRotationAngle, mapPixelsToSheet, worldToMapPixels } from './SheetTransforms.js';
import { SheetSyncEvents, ROTATION_DURATION, WAIT_BEFORE_ROTATION } from './SheetSyncConstants.js';
import ThumbnailsOverlay from './ThumbnailsOverlay.js';

const av = Autodesk.Viewing;
const ProgressState = av.ProgressState;
const namespace = AutodeskNamespace('Autodesk.AEC');
const myExtensionName = 'Autodesk.AEC.TopViewRendererExtension';

const tempVec3 = new THREE.Vector3();
const tempVec2 = new THREE.Vector2();
const box = new THREE.Box3();

/**
// TopViewRenderer - A viewer extension renders a 3D top view of a target model.
//                   It is highly recommended to use LeechViewer for the target viewer - otherwise the 3D model will be loaded again,
//                   and no shared resources will be used.
//
// @param {Viewer3D} [viewer]                       - viewer instance
// @param {Object} [options]                        - Configuration options for TopViewRenderer
*/
export default class TopViewRenderer extends Autodesk.Viewing.Extension {
    constructor(viewer, options = {}) {
        super(viewer, options);

        this.options = this.options.topViewRendererOptions || this.options;
        this.disableAnimations = this.options.disableAnimations ?? false;

        // [modelKey -> boolean] map that represents if a model is being currently loaded on the topViewViewer.
        this.isModelLoadingMap = {};

        // Time to wait before start rotating to sheet origin.
        this.waitBeforeRotation = this.disableAnimations ? 0 : WAIT_BEFORE_ROTATION;

        // Rotation speed.
        this.rotationDuration = this.disableAnimations ? 0 : ROTATION_DURATION;

        this.onBeginScene = this.onBeginScene.bind(this);
        this.update = this.update.bind(this);
        this.onMainMapChanged = this.onMainMapChanged.bind(this);
        this.onResize = this.onResize.bind(this);
        this.rotateToSheet = this.rotateToSheet.bind(this);
        this.onSheetGeometryLoaded = this.onSheetGeometryLoaded.bind(this);
        this.updateBackgroundTexture = this.updateBackgroundTexture.bind(this);
        this.onCameraChanged = this.onCameraChanged.bind(this);

        // Callbacks to be invoked at the end of rotation
        this.pendingFinishedCallbacks = [];
    }

    async load() {
        this.sheetSyncExt = await this.viewer.loadExtension('Autodesk.AEC.SheetSyncExtension', this.viewer.config);
        if (!this.sheetSyncExt) {
            return false;
        }

        this.targetViewer = this.sheetSyncExt.targetViewer;

        this.initTopViewViewer();

        if (this.options.minimapDebugMode) {
            const NearFarOverlay = require('./NearFarOverlay.js').default;
            this.nearFarOverlay = new NearFarOverlay(this.topViewViewer, this.targetViewer);

            const modelQueue = this.topViewViewer.impl.modelQueue();
            const originalReset = modelQueue.reset.bind(modelQueue);

            modelQueue.reset = (...args) => {
                // In order to present frustum culling in the top view minimap, we need to "steal" the target viewer's camera when calculating the frustum.
                args[0] = this.targetViewer.getCamera();
                originalReset(...args);
            };

        } else {
            this.thumbnailsOverlay = new ThumbnailsOverlay(this.topViewViewer, this.sheetSyncExt);
        }

        this.addListeners();

        return true;
    }

    initTopViewViewer() {
        const container = this.viewer.container;
        const sharedResources = this.targetViewer.sharedResources;

        const config = { childViewer: true };
        if (this.targetViewer.config?.profileSettings) {
            config.profileSettings = Object.assign({}, this.targetViewer.config.profileSettings);
        }

        // In case targetViewer is a LeechViewer, create a leechViewer and use the shared resources.
        if (sharedResources) {
            this.topViewViewer = Autodesk.Viewing.createLeechViewer(container, config, sharedResources, Autodesk.Viewing.Viewer3D);
        } else {
            // Otherwise, it's unfortunate, and you'll have to create a regular Viewer3D, without resource sharing.
            console.warn('TopViewRenderer - It is highly recommended using LeechViewer as your target viewer when using this extension.');
            this.topViewViewer = new Autodesk.Viewing.Viewer3D(container, config);
        }

        this.viewer.container.insertBefore(this.topViewViewer.container, this.viewer.container.firstChild);
        this.backgroundOpacityBackup = this.viewer.impl.renderer().getClearAlpha();

        if (this.isCanvasOverlaySupported()) {
            // Make sheet viewer background transparent so we'll be able to see the bottom viewer behind it.
            this.viewer.setBackgroundOpacity(0);
        } else {
            // In case that canvas overlay is not supported, we don't want to actually display the topView viewer.
            // Instead, we'll use it as a background texture.
            this.topViewViewer.container.style.opacity = 0;
        }

        this.topViewViewer.container.style.zIndex = -1;
        this.topViewViewer.container.style.pointerEvents = 'none';
        if (!this.disableAnimations) {
            this.topViewViewer.container.style.transition = 'opacity 0.7s'; // Value matches "FADE_DURATION" inside SheetSyncConstants.js
        }
        this.topViewViewer.container.style.backgroundColor = 'transparent';
        this.topViewViewer.start(undefined, undefined, undefined, undefined, { webglInitParams: { alpha: true }});
        this.topViewViewer.impl.skipCameraUpdate = true; // Used in order to set near-far values manually.
        this.topViewViewer.setBackgroundOpacity(0);

        this.backgroundColorBackup = this.viewer.container.style.backgroundColor;
        this.viewer.container.style.backgroundColor = 'transparent';
    }

    destroyTopViewViewer() {
        if (this.topViewViewer) {
            this.topViewViewer.finish();
            this.topViewViewer = null;
            this.viewer.container.style.backgroundColor = this.backgroundColorBackup;
            this.viewer.setBackgroundOpacity(this.backgroundOpacityBackup);
        }
    }

    unload() {
        this.removeListeners();

        if (this.thumbnailsOverlay) {
            this.thumbnailsOverlay.destroy();
            this.thumbnailsOverlay = null;
        }

        if (this.nearFarOverlay) {
            this.nearFarOverlay.destroy();
            this.nearFarOverlay = null;
        }

        this.destroyTopViewViewer();

        if (this.rotationAnim) {
            this.rotationAnim.stop();
            this.rotationAnim = null;
        }

        this.rotationTimeout && clearTimeout(this.rotationTimeout);

        this.viewer = null;
        this.targetViewer = null;
        this.sheetSyncExt = null;

        this.pendingFinishedCallbacks = [];

        return true;
    }

    addListeners() {
        this.targetViewer.addEventListener(av.NAVIGATION_MODE_CHANGED_EVENT, this.update);
        this.targetViewer.addEventListener(av.MODEL_ADDED_EVENT, this.update);
        this.targetViewer.addEventListener(av.MODEL_REMOVED_EVENT, this.update);
        this.targetViewer.addEventListener(Autodesk.AEC.LevelsExtension.LEVEL_CHANGED, this.update);
        this.viewer.addEventListener(av.PROGRESS_UPDATE_EVENT, this.onBeginScene);
        this.viewer.addEventListener(av.RENDER_SCENE_PART, this.update);
        this.viewer.addEventListener(av.VIEWER_RESIZE_EVENT, this.onResize);
        this.viewer.addEventListener(SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, this.onMainMapChanged);
        this.viewer.addEventListener(av.GEOMETRY_LOADED_EVENT, this.onSheetGeometryLoaded);
        this.topViewViewer.addEventListener(av.MODEL_ADDED_EVENT, this.update);

        if (!this.isCanvasOverlaySupported()) {
            this.topViewViewer.addEventListener(Autodesk.Viewing.RENDER_PRESENTED_EVENT, this.updateBackgroundTexture, { priority: -2000 }); // Priority is needed here in case we use LeechViewer.
            this.viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChanged);
        }

        const levelExt = this.targetViewer.getExtension('Autodesk.AEC.LevelsExtension');
        levelExt && levelExt.floorSelector && levelExt.floorSelector.addEventListener(Autodesk.AEC.FloorSelector.SELECTED_FLOOR_CHANGED, this.update);

        // In minimap debug mode - we need to render the minimap even when there is no actual movement in the top view.
        // This is needed in order to update the frustum according to target viewer.
        if (this.options.minimapDebugMode) {
            this.targetViewer.addEventListener(av.PROGRESS_UPDATE_EVENT, this.onBeginScene);
        }
    }

    removeListeners() {
        this.targetViewer.removeEventListener(av.NAVIGATION_MODE_CHANGED_EVENT, this.update);
        this.targetViewer.removeEventListener(av.MODEL_ADDED_EVENT, this.update);
        this.targetViewer.removeEventListener(av.MODEL_REMOVED_EVENT, this.update);
        this.targetViewer.removeEventListener(Autodesk.AEC.LevelsExtension.LEVEL_CHANGED, this.update);
        this.viewer.removeEventListener(av.PROGRESS_UPDATE_EVENT, this.onBeginScene);
        this.viewer.removeEventListener(av.RENDER_SCENE_PART, this.update);
        this.viewer.removeEventListener(av.VIEWER_RESIZE_EVENT, this.onResize);
        this.viewer.removeEventListener(SheetSyncEvents.MAIN_MAP_CHANGED_EVENT, this.onMainMapChanged);
        this.viewer.removeEventListener(av.GEOMETRY_LOADED_EVENT, this.onSheetGeometryLoaded);
        this.topViewViewer.removeEventListener(av.MODEL_ADDED_EVENT, this.update);

        if (!this.isCanvasOverlaySupported()) {
            this.topViewViewer.removeEventListener(Autodesk.Viewing.RENDER_PRESENTED_EVENT, this.updateBackgroundTexture);
            this.viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChanged);
        }

        const levelExt = this.targetViewer.getExtension('Autodesk.AEC.LevelsExtension');
        levelExt && levelExt.floorSelector && levelExt.floorSelector.removeEventListener(Autodesk.AEC.FloorSelector.SELECTED_FLOOR_CHANGED, this.update);

        if (this.options.minimapDebugMode) {
            this.targetViewer.removeEventListener(av.PROGRESS_UPDATE_EVENT, this.onBeginScene);
        }
    }

    // Make sure that when the main 2D viewer is resizing - topViewViewer updates as well.
    onResize({ width, height }) {
        this.topViewViewer.impl.resize(width, height, true);
        this.update();
    }

    onMainMapChanged() {
        this.syncCamera();
        this.update();
        this.validateRotation();
    }

    onSheetGeometryLoaded() {
        this.validateRotation();
    }

    validateRotation() {
        this.rotationTimeout && clearTimeout(this.rotationTimeout);
        this.rotationTimeout = setTimeout(this.rotateToSheet, this.waitBeforeRotation * 1000);
    }

    isDraggingAvatar() {
        const avatarExt = this.viewer.getExtension('Autodesk.AEC.AvatarExtension');
        const isDraggingAvatar = !avatarExt || !avatarExt.avatarWidget || avatarExt.avatarWidget.isDragging();

        return isDraggingAvatar;
    }

    rotateToSheet() {
        if (!this.viewer) {
            return;
        }

        // Rotate only if sheet is shown. Otherwise there is no reason to rotate.
        if (!this.sheetSyncExt.getShowSheet()) {
            return;
        }

        if (this.rotationAnim) {
            this.rotationAnim.stop();
            this.rotationAnim = null;
        }

        // In case the avatar is currently being dragged - don't rotate yet, cause it's annoying.
        if (this.isDraggingAvatar()) {
            // Postpone rotation to the next timeout.
            this.validateRotation();
            return;
        }

        let startAngle = getCameraRotationAngle(this.viewer);

        // Find shortest rotation (keep angle between -PI and PI)
        if (startAngle < -Math.PI) {
            startAngle += 2 * Math.PI;
        } else if (startAngle > Math.PI) {
            startAngle -= 2 * Math.PI;
        }

        // No rotation needed.
        if (startAngle === 0) {
            return;
        }

        const onTimer = (t) => {
            t = Autodesk.Viewing.Private.smootherStep(t);
            const rotation = startAngle * (1 - t);
            const camera = this.viewer.getCamera();
            camera.up.set(0, 1, 0);
            rotateAround(camera.up, tempVec3, rotation);
            camera.dirty = true;
            camera.updateCameraMatrices();
        };

        const onRotationEnd = () => {
            this.rotationAnim = null;

            this.pendingFinishedCallbacks.forEach(cb => cb());
            this.pendingFinishedCallbacks = [];
        };

        this.rotationAnim = Autodesk.Viewing.Private.fadeValue(0, 1, this.rotationDuration, onTimer, onRotationEnd);
    }

    waitForRotationDone() {
        const promise = new Promise(resolve => {
            // In case there is no active rotation, resolve immediately.
            if (!this.rotationAnim) {
                return resolve();
            }

            // Otherwise, wait for rotation callback to be called.
            this.pendingFinishedCallbacks.push(resolve);
        });

        return promise;
    }

    // Sync top view whenever the 2d sheet starts rendering.
    onBeginScene({ percent, state}) {
        if (percent === 0 && state === ProgressState.RENDERING) {
            this.update();
        }
    }

    // Jump the camera to make the illusion that we keep the same position, scale and rotation while switching to a different sheetMap.
    syncCamera() {
        if (!this.sheetSyncExt || !this.sheetSyncExt.sheetMap || !this.viewer.model || !this.worldBoundingBox) {
            return;
        }

        const camera = this.viewer.getCamera();
        const map = this.sheetSyncExt.sheetMap;

        const viewportLowerLeft = mapPixelsToWorld(map, map.viewportBounds.min.x, map.viewportBounds.min.y, 0);
        const viewportLowerRight = mapPixelsToWorld(map, map.viewportBounds.max.x, map.viewportBounds.min.y, 0);

        let angle = Math.atan2(viewportLowerRight.y-viewportLowerLeft.y, viewportLowerRight.x-viewportLowerLeft.x);

        // Rotate bounding box according to the map's rotation.
        const center = this.worldBoundingBox.getCenter(new THREE.Vector3());
        rotateAround(this.worldBoundingBox.min, center, angle);
        rotateAround(this.worldBoundingBox.max, center, angle);

        const p0 = worldToMapPixels(map, this.worldBoundingBox.min.clone());
        const p1 = worldToMapPixels(map, new THREE.Vector3(this.worldBoundingBox.min.x, this.worldBoundingBox.max.y, 0));
        const p2 = worldToMapPixels(map, this.worldBoundingBox.max.clone());

        p0.z = 0;
        p1.z = 0;
        p2.z = 0;

        mapPixelsToSheet(map, p0);
        mapPixelsToSheet(map, p2);
        mapPixelsToSheet(map, p1);

        box.setFromPoints([p0, p2]);

        camera.setViewFromBox(box, true);

        const worldAngle = getCameraRotationAngle(this.topViewViewer);
        rotateAround(camera.up, tempVec3, worldAngle - angle);

        camera.dirty = true;
        camera.updateCameraMatrices();

        this.viewer.impl.invalidate(true);

        // Restart rotation if it's during rotation.
        if (this.rotationAnim) {
            this.rotateToSheet();
        }
    }

    // Setup a virtual camera according to sheetMap's bounds.
    // This camera should be used in the 3D scene in order to create a top view screenshot.
    updateTopViewViewerCamera() {
        // If there is no 2D model loaded, worldBounds are infinity, and it will cause calculation errors here.
        // For this reason, we have to wait until there is a loaded model.
        if (!this.viewer.model) {
            return;
        }

        const currentLevel = this.sheetSyncExt.getCurrentLevel();

        const levelExt = this.targetViewer.getExtension('Autodesk.AEC.LevelsExtension');
        const zRangeMax = currentLevel ? levelExt.getZRange(currentLevel.index).zMax : Infinity;

        const renderer = this.viewer.impl.renderer();
        const canvasWidth = renderer.settings.logicalWidth;
        const canvasHeight = renderer.settings.logicalHeight;
        const up = this.viewer.getCamera().up.clone();

        // Normally, widgetToMapPixels is used with event pixel coords (which are integer in x/y).
        // Here, we need the input coords to be interpreted as accurate float-positions within [0, widgetWidth] x [0, widgetHeight] instead.
        // Otherwise, we get a half-pixel shift when computing the camera.
        const usePixelCenter = false;

        // get position within map widget
        const p0 = tempVec2.set(0, canvasHeight); // lower-left corner
        widgetToMapPixels(this.sheetSyncExt.sheetMap, this.viewer, p0, false, usePixelCenter);
        const p0World = mapPixelsToWorld(this.sheetSyncExt.sheetMap, p0.x, p0.y, 0);

        const p1 = tempVec2.set(canvasWidth, canvasHeight); // lower-right corner
        widgetToMapPixels(this.sheetSyncExt.sheetMap, this.viewer, p1, false, usePixelCenter);
        const p1World = mapPixelsToWorld(this.sheetSyncExt.sheetMap, p1.x, p1.y, 0);

        const p2 = tempVec2.set(canvasWidth, 0); // upper-right corner
        widgetToMapPixels(this.sheetSyncExt.sheetMap, this.viewer, p2, false, usePixelCenter);
        const p2World = mapPixelsToWorld(this.sheetSyncExt.sheetMap, p2.x, p2.y, 0);

        // First set the box in order to get its center. It's needed for calculating the rotation.
        box.setFromPoints([p0World, p2World]);
        const center = box.getCenter(new THREE.Vector3());

        // calculate world angle according the world direction from lower left corner, to lower right corner.
        const cameraAngle = getCameraRotationAngle(this.viewer);
        const worldAngle = Math.atan2(p1World.y - p0World.y, p1World.x - p0World.x);

        rotateAround(p0World, center, -worldAngle);
        rotateAround(p2World, center, -worldAngle);
        rotateAround(up, tempVec3, worldAngle - cameraAngle);

        // Bounding box rotated according to camera rotation.
        box.setFromPoints([p0World, p2World]);

        // Save worldBoundingBox for later use - in order to sync the camera position.
        this.worldBoundingBox = box.clone();

        const worldBounds = this.targetViewer.impl.getVisibleBounds();

        // The near and far plans are determined according to these values.
        const zMax = Math.min(zRangeMax, worldBounds.max.z);
        const zMin = worldBounds.min.z;

        // Set camera according to the bounding box.
        const camera = this.topViewViewer.getCamera();

        const cameraDirty = setTopView(camera, box, zMin, zMax, box.getSize(new THREE.Vector3()).y, up);

        // Setting camera dirty changes the _needsClear flag in viewerImpl
        // If we'll do that during forced rendering, it will trigger an infinite loop.
        if (!this.forcedRerender) {
            // Rerendering is expensive so make sure the camera really has changed
            camera.dirty || (camera.dirty = cameraDirty);
        }
    }

    unloadIrrelevant3DModels() {
        if (!this.sheetSyncExt?.targetModels) {
            return;
        }

        const visibleModelsKeys = this.sheetSyncExt.targetModels.map(model => model.getModelKey());

        this.topViewViewer.getVisibleModels().forEach((model) => {
            const key = model.getModelKey();
            if (visibleModelsKeys.indexOf(key) === -1) {
                this.topViewViewer.unloadModel(model);
                this.isModelLoadingMap[key] = false;
            }
        });
    }

    loadRelevant3DModels() {
        if (!this.sheetSyncExt.targetModels) {
            return;
        }

        const visibleModelsKeys = this.topViewViewer.getVisibleModels().map(model => model.getModelKey());

        this.sheetSyncExt.targetModels.forEach((model) => {
            const key = model.getModelKey();

            if (visibleModelsKeys.indexOf(key) === -1 && !this.isModelLoadingMap[key]) {
                this.isModelLoadingMap[key] = true;
                const loadOptions = model.getData().loadOptions;
                this.topViewViewer.loadDocumentNode(
                    model.getDocumentNode().getRootNode().getDocument(),
                    model.getDocumentNode(),
                    {
                        keepCurrentModels: true,
                        preserveView: true,
                        globalOffset: loadOptions.globalOffset,
                        applyRefPoint: loadOptions.applyRefPoint,
                        placementTransform: loadOptions.placementTransform,
                        applyPlacementInModelUnits: loadOptions.applyPlacementInModelUnits
                    }
                ).then(() => {
                    // Set topView render config.
                    const config = this.targetViewer.impl.renderer().getConfig();
                    config.renderEdges = true;
                    config.envMapBackground = null;
                    config.aoEnabled = false;
                    config.antialias = true;

                    // In WebGPU, sharing the cubemap is possible, because the renderers only share the same device.
                    // It's also required - otherwise all turns black due the environment lighting.
                    if (!Autodesk.Viewing.Private.USE_WEBGPU) {
                        // In WebGL, we can't share this resource between viewers.
                        // TODO: For WebGPU, doing this would turn everything to black. For WebGL, it's a bit unclear why
                        //       we don't have this problem too. When using MultiViewer framework, the reason
                        //       might just be that it just "accidentally" still inherits the cubemap of the
                        //       main viewer. This needs some further investigation to make it more consistent and clearer. (VIZX-2233).
                        config.envMap = null;
                    }
                    this.topViewViewer.impl.renderer().applyConfig(config);
                    this.topViewViewer.setBackgroundOpacity(0);
                });
            }
        });
    }

    hideTopViewViewer() {
        if (this.topViewViewer) this.topViewViewer.container.style.opacity = 0;
    }

    showTopViewViewer() {
        if (this.topViewViewer && this.isCanvasOverlaySupported()) this.topViewViewer.container.style.opacity = 1;
    }

    update() {
        if (!this.sheetSyncExt || !this.sheetSyncExt.sheetMap || !this.viewer || !this.targetViewer || !this.topViewViewer) {
            // Remove unused 3D models from topViewViewer.
            this.unloadIrrelevant3DModels();

            this.hideTopViewViewer();
            return;
        }

        this.showTopViewViewer();

        // Remove unused 3D models from topViewViewer.
        this.unloadIrrelevant3DModels();

        // Load relevant 3D models to toViewViewer.
        this.loadRelevant3DModels();

        // Update top view camera, according to the current sheet map.
        this.updateTopViewViewerCamera();

        this.thumbnailsOverlay?.update();
    }

    // Notice that this callback should get called only when isCanvasOverlaySupported() is false.
    onCameraChanged() {
        // Prevent infinite loop.
        if (this.forcedRerender) {
            return;
        }

        // Force rendering topViewViewer at the current frame - so it will be ready as a background texture for the next frame.
        this.topViewViewer.impl.tick(performance.now());
    }

    // Notice that this callback should get called only when isCanvasOverlaySupported() is false.
    // When canvas overlay is not supported (one canvas with transparent background on top of the other),
    // We use the bottom viewer's canvas as a background texture for the upper viewer.
    // The result looks exactly the same. However, because of the extra needed tick, the first option is preferred when possible.
    updateBackgroundTexture() {
        if (!this.backgroundTexture) {
            this.backgroundTexture = new THREE.Texture();
            this.backgroundTexture.minFilter = THREE.LinearFilter;
            this.backgroundTexture.image = this.topViewViewer.canvas; // Use viewer's canvas as a background texture.
            this.viewer.impl.renderer().setBackgroundTexture(this.backgroundTexture);
        }

        this.forcedRerender = true;
        this.backgroundTexture.needsUpdate = true;
        this.viewer.impl.invalidate(true);
        this.viewer.impl.tick(performance.now());
        this.forcedRerender = false;
    }

    // Canvas overlay === one canvas with transparent background on top of the other.
    // iOS doesn't support canvas blending.
    isCanvasOverlaySupported() {
        return !Autodesk.Viewing.isIOSDevice();
    }
}

namespace.TopViewRenderer = TopViewRenderer;
av.theExtensionManager.registerExtension(myExtensionName, TopViewRenderer);
